import { Add, Campaign, Edit, Payments } from "@mui/icons-material";
import {
  Box,
  Divider,
  List,
  Stack,
  Typography,
  Grid2 as Grid,
  IconButton,
  Tooltip,
  Alert,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { ResponsiveButtonLink } from "components/Button";
import { MainLayoutContainer } from "components/Layout";
import {
  useAddProgramMutation,
  useGetOrganizationByIdLazyQuery,
  useGetOrganizationDetailByIdLazyQuery,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";
import { FC, useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { ORG_TYPE } from "types/organization";
import { ROLES } from "types/user";
import {
  GraduationClassItem,
  Item,
  OrganizationRequirements,
  SiteItem,
  UserItem,
} from "./components";
import dayjs from "dayjs";
import { Requirement } from "types/requirement";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { resetPasswordRequestThunk } from "reduxStore/auth/thunks";
import { showNotificationThunk } from "reduxStore/alert/thunks";
import { NotificationSeverity } from "helpers/constants";
import { useAppDispatch } from "hooks/useAppDispatch";
import { CustomLabels } from "components/CustomLabel/CustomLabels";
import { DomainTags } from "components/DomainTags";
import { TAG_TYPE_DOMAIN, Tag } from "types/tag";

export const OrganizationDetail: FC = () => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams();
  const { user } = useAuthUser();
  const location = useLocation();
  const [userTypeToInvite, setUserTypeToInvite] = useState("");
  const [inviteUserModal, setInviteUserModal] = useState(false);
  const [inviteTermsPendingOnly, setInviteTermsPendingOnly] = useState(false);

  const [getOrganization, { data }] = useGetOrganizationByIdLazyQuery();
  const [getOrganizationDetail, { data: organizationDetail }] =
    useGetOrganizationDetailByIdLazyQuery();
  const [addProgram] = useAddProgramMutation();

  const organization = useMemo(() => {
    return (
      data?.organization_by_pk || organizationDetail?.organization_by_pk || null
    );
  }, [data, organizationDetail]);

  const academicYear = useMemo(() => {
    return organizationDetail?.academic_year || [];
  }, [organizationDetail]);

  const graduationClass = useMemo(() => {
    return (
      organizationDetail?.graduation_class.map((graduationClass) => ({
        ...graduationClass,
        students: graduationClass.students.map((student) => student.student),
      })) || []
    );
  }, [organizationDetail]);

  const hasProgram = useMemo(() => {
    return (organizationDetail?.program?.length || 0) > 0;
  }, [organizationDetail]);

  const handleAddProgram = () => {
    if (organizationId) {
      addProgram({ variables: { orgId: organizationId } });
      dispatch(
        showNotificationThunk({
          message: "Program added successfully",
          severity: NotificationSeverity.success,
        }),
      );
      fetchOrganizationDetail();
    }
  };

  const customLabels = useMemo(() => {
    return (
      organizationDetail?.organization_by_pk?.custom_labels.map(
        (customLabel) => customLabel.custom_label,
      ) || []
    );
  }, [organizationDetail]);

  const domainTags = useMemo(() => {
    const orgTags =
      organizationDetail?.organization_by_pk?.tags ||
      data?.organization_by_pk?.tags ||
      [];
    return orgTags
      .map((row) => row.tag)
      .filter(
        (tag): tag is NonNullable<typeof tag> =>
          Boolean(tag) && tag.type === TAG_TYPE_DOMAIN,
      ) as Tag[];
  }, [organizationDetail, data]);

  const sites = useMemo(() => {
    return organization?.sites.map((site) => site.site);
  }, [organization]);

  const directors = useMemo(() => {
    return organization?.users
      .filter((user) => user.user.role === ROLES.DIRECTOR)
      .map((user) => user.user);
  }, [organization]);

  const managers = useMemo(() => {
    return organization?.users
      .filter((user) => user.user.role === ROLES.MANAGER)
      .map((user) => user.user);
  }, [organization]);

  const coaches = useMemo(() => {
    return organization?.users
      .filter((user) => user.user.role === ROLES.COACH)
      .map((user) => user.user);
  }, [organization]);

  const learners = useMemo(() => {
    const fromOrgId = organizationDetail?.learners ?? [];
    const fromGraduationClass = graduationClass.flatMap(
      (graduationClass) => graduationClass.students ?? [],
    );
    const combined = [...fromOrgId, ...fromGraduationClass].filter(
      (user): user is NonNullable<typeof user> => Boolean(user),
    );

    return combined
      .filter(
        (user, index, self) =>
          index === self.findIndex((item) => item.user_id === user.user_id),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organizationDetail, graduationClass]);

  const isDetail = useMemo(() => {
    return location.pathname.includes("/detail");
  }, [location.pathname]);

  const fetchOrganizationDetail = () => {
    if (organizationId && user?.role === ROLES.STUDENT) {
      getOrganization({
        variables: {
          organizationId,
        },
      });
    } else if (
      organizationId &&
      (user?.role === ROLES.ADMIN ||
        user?.role === ROLES.DIRECTOR ||
        user?.role === ROLES.MANAGER)
    ) {
      getOrganizationDetail({
        variables: {
          organizationId,
        },
      });
    }
  };

  useEffect(() => {
    if (organizationId && user) {
      fetchOrganizationDetail();
    }
  }, [organizationId, user]);

  const getUsersForBulkInvite = (
    type: string,
  ): Array<{ email: string; accepted_terms_of_use?: boolean }> => {
    let users: Array<{ email: string; accepted_terms_of_use?: boolean }> = [];
    switch (type) {
      case "directors":
        users = directors || [];
        break;
      case "managers":
        users = managers || [];
        break;
      case "coaches":
        users = coaches || [];
        break;
      case "learners":
        users = learners || [];
        break;
      default:
        return [];
    }

    if (inviteTermsPendingOnly) {
      return users.filter((member) => !member.accepted_terms_of_use);
    }

    return users;
  };

  const inviteModalMessage = useMemo(() => {
    if (!userTypeToInvite) return "";

    if (inviteTermsPendingOnly) {
      return `A formal email invitation will be sent to ${userTypeToInvite} who have not accepted the terms of use.`;
    }

    return `A formal email invitation will be sent to all ${userTypeToInvite} associated with this organization.`;
  }, [inviteTermsPendingOnly, userTypeToInvite]);

  const handleResendToAll = async (type: string) => {
    const usersToInvite = getUsersForBulkInvite(type);

    if (usersToInvite.length === 0) {
      dispatch(
        showNotificationThunk({
          message: inviteTermsPendingOnly
            ? "No users with pending terms of use to invite."
            : "No users available to invite.",
          severity: NotificationSeverity.info,
        }),
      );
      return;
    }

    for (const member of usersToInvite) {
      await dispatch(
        resetPasswordRequestThunk({
          email: member.email,
          isResendingInvite: true,
        }),
      );
    }

    dispatch(
      showNotificationThunk({
        message: "Invitation sent successfully",
        severity: NotificationSeverity.success,
      }),
    );
  };

  const closeInviteModal = () => {
    setInviteUserModal(false);
    setInviteTermsPendingOnly(false);
  };

  return (
    <MainLayoutContainer
      title={organization?.name ?? ""}
      avatarSrc={organization?.logo_url ?? null}
      loading={!organization}
      breadcrumb={
        isDetail && user?.role !== ROLES.ADMIN
          ? null
          : BREADCRUMB_PAGE.ORGANIZATION
      }
      buttonActions={
        <Stack direction="row" spacing={2}>
          <Box sx={{ display: "none" }}>
            <ResponsiveButtonLink
              variant="outlined"
              to={`/organization/${organizationId}/edit`}
              icon={<Payments />}
            >
              View Billing & Payments
            </ResponsiveButtonLink>
          </Box>
          {(user?.role === ROLES.ADMIN ||
            user?.role === ROLES.DIRECTOR ||
            user?.role === ROLES.MANAGER) && (
            <ResponsiveButtonLink
              variant="contained"
              to={`/organization/${organizationId}/edit`}
              icon={<Edit />}
            >
              Edit
            </ResponsiveButtonLink>
          )}
        </Stack>
      }
    >
      <Box>
        <Box mt={4} mb={2}>
          {organizationDetail && !hasProgram && (
            <>
              {/* <Divider sx={{ my: 2 }} /> */}
              <Box my={2}>
                <Alert
                  severity="info"
                  action={
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleAddProgram}
                      startIcon={<Add />}
                    >
                      Add Program
                    </Button>
                  }
                >
                  No program configured.
                </Alert>
              </Box>
            </>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                {user?.role !== ROLES.STUDENT && user?.role !== ROLES.COACH && (
                  <Item label="Organization Type" value={organization?.type} />
                )}
                {user?.role !== ROLES.STUDENT && user?.role !== ROLES.COACH && (
                  <Item
                    label="Number of Users"
                    value={organization?.users.length}
                    sx={{ display: { xs: "block", md: "none" } }}
                  />
                )}

                <Item
                  label="Primary Contact Name"
                  value={organization?.primary_contact_name}
                />
                <Item
                  label="Primary Contact Email"
                  value={organization?.primary_contact_email}
                  sx={{ display: { xs: "block", md: "none" } }}
                />
                {organization?.secondary_contact_name && (
                  <Item
                    label="Secondary Contact Name"
                    value={organization?.secondary_contact_name}
                  />
                )}
                {organization?.secondary_contact_email && (
                  <Item
                    label="Secondary Contact Email"
                    value={organization?.secondary_contact_email}
                    sx={{ display: { xs: "block", md: "none" } }}
                  />
                )}
              </Stack>
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              <Stack spacing={1}>
                {user?.role !== ROLES.STUDENT && user?.role !== ROLES.COACH && (
                  <Item
                    label="Number of Users"
                    value={organization?.users.length}
                  />
                )}
                <Item
                  label="Primary Contact Email"
                  value={organization?.primary_contact_email}
                />
                {organization?.secondary_contact_email && (
                  <Item
                    label="Secondary Contact Email"
                    value={organization?.secondary_contact_email}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>

          {sites && sites.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box mt={2}>
                <Typography variant="h6" fontWeight={400}>
                  Sites
                </Typography>
              </Box>
              <Box mt={1} mb={2}>
                {sites?.map((site) => (
                  <SiteItem key={site.site_id} site={site} />
                ))}
              </Box>
            </>
          )}

          <Box>
            <Divider sx={{ my: 2 }} />

            <CustomLabels
              organizationId={organizationId}
              customLabels={customLabels}
              onAddCustomLabel={() => {
                if (organizationId) {
                  fetchOrganizationDetail();
                }
              }}
              displayHiddenButton={true}
              displayEditButton={false}
              isClinical={organization?.type === ORG_TYPE.CLINIC}
            />
          </Box>

          {organizationId && user?.role !== ROLES.STUDENT && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <DomainTags
                  organizationId={organizationId}
                  tags={domainTags}
                  isEditing={
                    user?.role === ROLES.ADMIN ||
                    user?.role === ROLES.DIRECTOR ||
                    user?.role === ROLES.MANAGER
                  }
                  onChanged={fetchOrganizationDetail}
                />
              </Box>
            )}

          {directors && directors.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" fontWeight={400}>
                  Directors
                </Typography>

                <Tooltip title="Resend Email To All Directors">
                  <IconButton
                    onClick={() => {
                      setUserTypeToInvite("directors");
                      setInviteUserModal(true);
                    }}
                    aria-label="resend-invite"
                    size={"large"}
                  >
                    <Campaign />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box mt={1} mb={2}>
                <List>
                  {directors?.map((user) => (
                    <UserItem key={user.user_id} user={user} />
                  ))}
                </List>
              </Box>
            </>
          )}

          {managers && managers.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" fontWeight={400}>
                  Managers
                </Typography>

                <Tooltip title="Resend Email To All Managers">
                  <IconButton
                    onClick={() => {
                      setUserTypeToInvite("managers");
                      setInviteUserModal(true);
                    }}
                    aria-label="resend-invite"
                    size={"large"}
                  >
                    <Campaign />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box my={2}>
                {managers?.map((user) => (
                  <UserItem key={user.user_id} user={user} />
                ))}
              </Box>
            </>
          )}

          {coaches && coaches.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" fontWeight={400}>
                  Coaches
                </Typography>

                <Tooltip title="Resend Email To All Coaches">
                  <IconButton
                    onClick={() => {
                      setUserTypeToInvite("coaches");
                      setInviteUserModal(true);
                    }}
                    aria-label="resend-invite"
                    size={"large"}
                  >
                    <Campaign />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box my={2}>
                {coaches?.map((user) => (
                  <UserItem key={user.user_id} user={user} />
                ))}
              </Box>
            </>
          )}

          {learners && learners.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" fontWeight={400}>
                  Learners
                </Typography>

                <Tooltip title="Resend Email To All Learners">
                  <IconButton
                    onClick={() => {
                      setUserTypeToInvite("learners");
                      setInviteUserModal(true);
                    }}
                    aria-label="resend-invite"
                    size={"large"}
                  >
                    <Campaign />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box my={2}>
                {learners.map((user) => (
                  <UserItem key={user.user_id} user={user} />
                ))}
              </Box>
            </>
          )}

          {academicYear.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight={400}>
                  Program
                </Typography>
                <Box my={2}>
                  {academicYear?.map((academicYear) => (
                    <Box key={academicYear.academic_year_id} ml={2}>
                      <Typography variant="body1">
                        {academicYear.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {dayjs(academicYear.start_date).format("ll")} -{" "}
                        {dayjs(academicYear.end_date).format("ll")}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}

          {graduationClass.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="h6" fontWeight={400}>
                  {organization?.type === ORG_TYPE.CLINIC
                    ? "Cohort"
                    : "Graduation Class"}
                </Typography>
                <Box my={2}>
                  {graduationClass?.map((graduationClass) => (
                    <GraduationClassItem
                      key={graduationClass.graduation_class_id}
                      graduationClass={graduationClass}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {organizationDetail?.organization_requirement && (
            <OrganizationRequirements
              requirements={
                organizationDetail?.organization_requirement?.map(
                  (req) => req.requirement as Requirement,
                ) || []
              }
            />
          )}
        </Box>
      </Box>

      {inviteUserModal && userTypeToInvite && (
        <ConfirmActionModal
          title={"Invite User"}
          message={inviteModalMessage}
          extraContent={
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={inviteTermsPendingOnly}
                  onChange={(event) =>
                    setInviteTermsPendingOnly(event.target.checked)
                  }
                  color="primary"
                />
              }
              label="Send only to users who have not accepted terms of use"
            />
          }
          show={inviteUserModal}
          onConfirm={() => handleResendToAll(userTypeToInvite)}
          onDismiss={closeInviteModal}
        />
      )}
    </MainLayoutContainer>
  );
};
